import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api"; // Your existing axios instance
import { hideLoader, showLoader } from "./loaderSlice";
import { showSnackBar } from "./snackSlice";

// Async Thunk - GET all vitals for a specific group
export const getAllVitals = createAsyncThunk(
  "vital/getAllVitals",
  async (roomId, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/getall/clinic/group/${roomId}/vitals`,
      );
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res.data.data;
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({ message: "Failed to fetch vitals", type: "error" }),
      );
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

// Async Thunk - POST create vital for a specific group
export const createVital = createAsyncThunk(
  "vital/createVital",
  async ({ roomId, formData }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/create/clinic/group/${roomId}/vital`,
        formData,
      );
      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(hideLoader());
        thunkAPI.dispatch(
          showSnackBar({
            message: "Vital created successfully!",
            type: "success",
          }),
        );
        return res.data.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create vital");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({ message: "Failed to create vital", type: "error" }),
      );
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

// Async Thunk - PUT update vital
export const updateVital = createAsyncThunk(
  "vital/updateVital",
  async (formData, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const { vitalId, ...payload } = formData;
      const res = await api({ contentType: true, auth: true }).put(
        `/update/clinic/vital/${vitalId}`,
        payload,
      );
      thunkAPI.dispatch(hideLoader());

      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Vital updated successfully!",
            type: "success",
          }),
        );
        return res.data.data;
      } else {
        return thunkAPI.rejectWithValue("Failed to update vital");
      }
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({ message: "Failed to update vital", type: "error" }),
      );
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

// Async Thunk - DELETE vital
export const deleteVital = createAsyncThunk(
  "vital/deleteVital",
  async (vitalId, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(
        `/delete/clinic/vital/${vitalId}`,
      );
      thunkAPI.dispatch(hideLoader());

      if (res.status === 200 || res.status === 204) {
        thunkAPI.dispatch(
          showSnackBar({
            message: "Vital deleted successfully!",
            type: "success",
          }),
        );
        return vitalId;
      } else {
        return thunkAPI.rejectWithValue("Failed to delete vital");
      }
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(
        showSnackBar({ message: "Failed to delete vital", type: "error" }),
      );
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

// Slice
const vitalSlice = createSlice({
  name: "vital",
  initialState: {
    vitals: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllVitals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllVitals.fulfilled, (state, action) => {
        state.vitals = action.payload;
        // .map((item) => ({
        //   ...item,
        //   referenceRanges:
        //     item?.lowerRange?.length > 0 || item?.upperRange?.length > 0
        //       ? [
        //           {
        //             species: item.rrSpecies || "",
        //             lower: item.lowerRange || "",
        //             upper: item.upperRange || "",
        //           },
        //         ]
        //       : [],
        // }));
        state.loading = false;
      })
      .addCase(getAllVitals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createVital.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVital.fulfilled, (state, action) => {
        const item = action.payload;
        const vital = {
          ...item,
          referenceRanges:
            item.lowerRange?.length > 0 || item.upperRange?.length > 0
              ? [
                  {
                    species: item.rrSpecies || "",
                    lower: item.lowerRange || "",
                    upper: item.upperRange || "",
                  },
                ]
              : [],
        };
        state.vitals.push(vital);
        state.loading = false;
      })
      .addCase(createVital.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateVital.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVital.fulfilled, (state, action) => {
        const updatedItem = action.payload;
        const index = state.vitals.findIndex(
          (v) => v.vitalId === updatedItem.vitalId,
        );
        if (index !== -1) {
          state.vitals[index] = {
            ...updatedItem,
            referenceRanges:
              updatedItem?.lowerRange?.length > 0 ||
              updatedItem?.upperRange?.length > 0
                ? [
                    {
                      species: updatedItem.rrSpecies || "",
                      lower: updatedItem.lowerRange || "",
                      upper: updatedItem.upperRange || "",
                    },
                  ]
                : [],
          };
        }
        state.loading = false;
      })
      .addCase(updateVital.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteVital.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteVital.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.vitals = state.vitals.filter((v) => v.vitalId !== deletedId);
        state.loading = false;
      })
      .addCase(deleteVital.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default vitalSlice.reducer;
